import type {CourseId} from 'types/common';
import {formatCourseStartDate} from 'utils/courseDates';
import {useCourseDocOnce} from './useCourseDocOnce';

/**
 * Reads the `startDate` from the globally-readable `course/{courseId}`
 * Firestore document and formats it for display in the viewer's locale (e.g.
 * "October 12, 2026"). Sourcing the date from Firestore keeps copy that
 * mentions a course start date correct across cycles without a code change.
 *
 * This is a convenience wrapper for surfaces that need only the date. A surface
 * that also needs other course fields should call {@link useCourseDocOnce}
 * directly and format with `formatCourseStartDate`, so the document is read
 * once rather than once per field.
 *
 * @param courseId - Id of the `course/{courseId}` document to read.
 * @return The formatted start date in the viewer's locale, or an empty string
 *   while the document is loading, when it has no `startDate` set, or when the
 *   read fails. Callers should render a date-free fallback in that case.
 * @throws Never — a read failure is logged and yields ''.
 */
export const useCourseStartDateLabel = (courseId: CourseId): string => {
  return formatCourseStartDate(useCourseDocOnce(courseId)?.startDate);
};
